import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { createDefaultSettings } from '@/db/defaults'
import { executeDatabaseOperation } from '@/db/errors'
import { settingsSchema } from '@/db/schemas'
import type { Settings } from '@/db/types'
import { invalidateBackupStatus } from '@/db/services/backupStatusService'
import { normalizePhone } from '@/db/normalization'
import { changedFields, writeAuditLog } from './helpers'

export type SettingsUpdate = Partial<
  Pick<
    Settings,
    | 'companyName'
    | 'phone'
    | 'instagram'
    | 'email'
    | 'logo'
    | 'qrCodeTarget'
    | 'lastBackupAt'
    | 'photoMaxInputBytes'
    | 'photoMaxDimension'
    | 'photoJpegQuality'
  >
>

export class SettingsRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async get(): Promise<Settings> {
    return executeDatabaseOperation('consultar configurações', async () => {
      const settings = await this.database.settings.get('app')
      return settingsSchema.parse(settings ?? createDefaultSettings())
    })
  }

  async update(input: SettingsUpdate): Promise<Settings> {
    const settings = await executeDatabaseOperation('editar configurações', async () =>
      this.database.transaction(
        'rw',
        [this.database.settings, this.database.auditLogs],
        async () => {
          const existing = settingsSchema.parse(
            (await this.database.settings.get('app')) ?? createDefaultSettings(),
          )
          const now = new Date().toISOString()
          const settings = settingsSchema.parse({
            ...existing,
            ...input,
            ...(input.phone !== undefined ? { phone: normalizePhone(input.phone) } : {}),
            updatedAt: now,
          })
          await this.database.settings.put(settings)
          await writeAuditLog(this.database, 'settings', 'app', 'update', changedFields(input), now)
          return settings
        },
      ),
    )
    invalidateBackupStatus(this.database)
    return settings
  }
}
